import { use, useEffect, useRef, useState } from "react";
import { getUserAlbum, deletePhoto, getPhotoUrlBatch, getPhotoPredictionStatus } from "../../services/photoService";
import { Photo } from "../../types/photo";
import { useModal } from "../../contexts/ModalContext";
import { useAlbum } from "../../contexts/AlbumContext";
import { Utils } from "../../utilities/Utils";
import { optimizeImage } from "../../utilities/imageOptimization";

import { MdVerified } from "react-icons/md";
import { VscUnverified } from "react-icons/vsc";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { FiMenu } from "react-icons/fi";
import { BiSolidDetail } from "react-icons/bi";
import { MdDeleteForever } from "react-icons/md";
import { AiOutlineQuestionCircle } from "react-icons/ai";

const Album = () => {
  const {
    photos,
    currentPage,
    pageSize,
    totalCount,
    setPhotos,
    setTotalCount,
    updatePhoto,
    setCurrentPage,
    addPhoto,
    removePhoto
  } = useAlbum();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const { openModal } = useModal();

  const handleEdit = (photo: Photo) => {
    console.log(photo);
    openModal("photo_detail", { photoItem: photo }, "Photo Detail", "xl")
  };
  const handleDelete = async (photoId: number) => {
    if (window.confirm("Deleted the photo?")) {
      try {
        await deletePhoto(photoId);
        // Remove the photo from state
        removePhoto(photoId);
        window.alert("Deleted successfully!");
      } catch {
        setError("Failed to delete the photo.");
      }
    }
  };

  const StatusIcon = ({ icon, label }: { icon: any, label: any }) => (
    <div className="absolute bottom-2 right-2 group">
      {icon}
      <div className="absolute bottom-full right-0 mb-1 px-2 py-1 text-xs text-white bg-gray-800 rounded-md shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 whitespace-nowrap">
        {label}
      </div>
    </div>
  );

  useEffect(() => {
    const fetchAlbum = async () => {
      try {
        const { photos, totalCount } = await getUserAlbum(currentPage, pageSize);
        const photoIds = photos.map((p) => p.photoId);
        const urls = await getPhotoUrlBatch(photoIds);
        const photosWithUrls = photos.map((p) => ({
          ...p,
          // Optimize images for card/grid display
          src: optimizeImage(urls[p.photoId], 'card'),
        }));

        setPhotos(photosWithUrls);
        setTotalCount(totalCount);
        setTotalPages(Math.ceil(totalCount / pageSize));
      } catch (err) {
        setError("Failed to fetch photos");
      } finally {
        setLoading(false);
      }
    };

    fetchAlbum();
  }, [currentPage]);

  if (loading)
    return (
      <div className="flex justify-center mt-10">
        <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );

  if (error)
    return (
      <p className="text-negative text-center mt-10 font-semibold">{error}</p>
    );

  return (
    <div id="album" className="relative">
      <div className="py-5 mx-auto">
        <h2 className="text-2xl font-bold text-white text-center mb-6">
          My Photo Album
        </h2>
        <div className="w-24 h-1 bg-white mx-auto my-4"></div>
        {photos.length === 0 &&
          <div className="flex flex-col text-xl font-bold text-white text-center mb-6 relative shadow-lg overflow-hidden transition-all duration-700 ease-out translate-y-5 w-1/2 md:w-2/5 m-auto py-16"
            style={{
              borderRadius: "95% 4% 92% 5%/4% 95% 6% 95%",
              border: `4px solid white`,
              borderStyle: 'dashed'
            }}>It's time to
            <button className="flex items-center custom-btn m-auto"
              onClick={() => openModal("upload", {}, "Upload", "xl")}>
              Upload
            </button> your first photo!
          </div>
        }

        {photos.length > 0 &&
          <div>
            <div
              className="grid p-6 rounded-lg grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
              ref={containerRef}
            >
              {photos.map((photo) => (
                <div
                  key={photo.photoId}
                  className="relative shadow-lg overflow-hidden transition-all duration-700 ease-out opacity-0 translate-y-5 hover:z-10 hover:ring-4 hover:ring-primary hover:ring-opacity-40"
                  ref={(el) => {
                    if (!el) return;
                    const observer = new IntersectionObserver(([entry]) => {
                      if (entry.isIntersecting) {
                        el.classList.remove("opacity-0", "translate-y-5");
                        el.classList.add("opacity-100", "translate-y-0");
                        observer.disconnect();
                      }
                    });
                    observer.observe(el);
                  }}
                  style={{
                    borderRadius: "95% 4% 92% 5%/4% 95% 6% 95%",
                    border: `4px solid white`,
                  }}
                >
                  <div className="absolute top-2 right-2">
                    <div className="relative group">
                      <button className="p-1 bg-black/60 text-white rounded-full hover:bg-black/80">
                        <FiMenu />
                      </button>

                      <div className="absolute right-0 mt-2 w-32 text-primary bg-white rounded-md shadow-lg z-20
                  opacity-0 invisible group-hover:opacity-100 group-hover:visible
                  transition-all duration-200">
                        <button
                          onClick={() => handleEdit(photo)}
                          className="block w-full px-4 py-2 text-left text-sm rounded-md hover:bg-gray-300 transition"
                        >
                          View Details <BiSolidDetail className="inline-block" />
                        </button>
                        <button
                          onClick={() => handleDelete(photo.photoId)}
                          className="block w-full px-4 py-2 text-left text-sm rounded-md text-negative hover:bg-gray-300 transition"
                        >
                          Delete Photo <MdDeleteForever className="inline-block text-negative" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <img
                    src={photo.src}
                    alt={photo.title}
                    loading="lazy"
                    className="w-full h-52 object-cover"
                  />
                  <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-black/90 to-transparent p-3 text-white">
                    <p className="font-semibold text-sm">{photo.title}</p>
                    <p className="text-xs">{Utils.formatDate(photo.uploadTime)}</p>
                    {/* Still being processed by ML modal */}
                    {getPhotoPredictionStatus(photo) === 'processing' && (
                      <StatusIcon icon={<VscUnverified className="text-yellow-400" />} label={"Analyzing Image"} />
                    )}
                    {/* Has high score prediction result */}
                    {getPhotoPredictionStatus(photo) === 'verified' && (
                      <StatusIcon icon={<MdVerified className="text-primary" />} label={"Koala Detected"} />
                    )}
                    {/* Has low score prediction result */}
                    {getPhotoPredictionStatus(photo) === 'uncertain' && (
                      <StatusIcon icon={<AiOutlineQuestionCircle className="text-yellow-400" />} label={"Uncertain"} />
                    )}
                    {/* No prediction result */}
                    {getPhotoPredictionStatus(photo) === 'not-found' && (
                      <StatusIcon icon={<AiOutlineCloseCircle className="text-negative" />} label={"No Koala Detected"} />
                    )}
                  </div>
                </div>
              ))}
            </div>
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-4 mt-4">
                <button
                  className="custom-btn disabled:opacity-50"
                  disabled={currentPage === 1}
                  onClick={() => {
                    setCurrentPage(currentPage - 1);
                    document.getElementById('album')?.scrollIntoView();
                  }}
                >
                  Prev
                </button>
                <span className="text-white">Page {currentPage} of {totalPages}</span>
                <button
                  className="custom-btn disabled:opacity-50"
                  disabled={currentPage === totalPages}
                  onClick={() => {
                    setCurrentPage(currentPage + 1);
                    document.getElementById('album')?.scrollIntoView();
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        }
      </div>
    </div>
  );
};

export default Album;
